import React, { useState, useEffect } from 'react';
import {
  X, ArrowRight, ArrowLeft, Check, Info, Shuffle, Cloud, Sparkles, Plug,
  Brain, Code, Palette, MessageCircle, Zap, Search, BrainCircuit, Image as ImageIcon, Lock,
} from 'lucide-react';
import { apiService } from '../../services/apiService';

interface AIModel {
  id: string;
  providerModelId: string;
  name: string;
  provider: string;
  description?: string;
  price_per_token?: string;
  capabilities?: Record<string, number>;
  requires_premium?: boolean;
  contextWindow?: number;
  maxTokens?: number;
  supports_vision?: boolean;
  supports_streaming?: boolean;
  functionCalling?: boolean;
  taskDeterminationModel?: boolean;
  task_determination_priority?: number;
  category?: string;
  ranked_position?: number;
  normalizedCost?: number;
  isUncensored?: boolean;
  isPrivacyCompliant?: boolean;
  isCustomEndpoint?: boolean;
  customEndpointUrl?: string;
  customApiKey?: string;
  customHeaders?: Record<string, string>;
}

interface ModelWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (model: AIModel) => Promise<string | null>;
  isAdmin: boolean;
  existingModels?: AIModel[];
}

type WizardStep = 'essentials' | 'capabilities' | 'endpoint' | 'review';

export default function ModelWizard({ isOpen, onClose, onSave, isAdmin, existingModels = [] }: ModelWizardProps) {
  // Calculate next rank position (highest + 1)
  const getNextRankPosition = () => {
    if (existingModels.length === 0) return 1;
    const maxRank = Math.max(...existingModels.map(m => m.ranked_position || 0));
    return maxRank + 1;
  };
  const [currentStep, setCurrentStep] = useState<WizardStep>('essentials');
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const [model, setModel] = useState<AIModel>({
    id: '',
    providerModelId: '',
    name: '',
    provider: isAdmin ? 'openrouter' : 'custom',
    description: '',
    price_per_token: '',
    capabilities: {
      webSearch: 0,
      enhancedReasoning: 0,
      reasoning: 0,
      coding: 0,
      creativity: 0,
      conversational: 0,
      speed: 0,
      imageGeneration: 0
    },
    contextWindow: 8192,
    maxTokens: 4096,
    supports_vision: false,
    supports_streaming: true,
    functionCalling: false,
    taskDeterminationModel: false,
    task_determination_priority: 0,
    category: '',
    ranked_position: getNextRankPosition(),
    normalizedCost: 0,
    requires_premium: false,
    isUncensored: false,
    isPrivacyCompliant: false,
    isCustomEndpoint: !isAdmin,
    customEndpointUrl: '',
    customApiKey: '',
    customHeaders: {}
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('essentials');
      setError('');
      setConnectionStatus('idle');
    }
  }, [isOpen]);

  const steps: WizardStep[] = model.provider === 'custom'
    ? ['essentials', 'capabilities', 'endpoint', 'review']
    : ['essentials', 'capabilities', 'review'];

  const currentStepIndex = steps.indexOf(currentStep);
  const totalSteps = steps.length;

  const handleFieldChange = (field: string, value: any) => {
    let processedValue = value;

    if (field === 'providerModelId') {
      setModel(prev => ({ ...prev, id: value, providerModelId: value }));
      return;
    }

    if (field === 'provider') {
      const isCustom = value === 'custom';
      setModel(prev => ({
        ...prev,
        provider: value,
        isCustomEndpoint: isCustom
      }));
      return;
    }

    setModel(prev => ({ ...prev, [field]: processedValue }));
  };

  const handleCapabilityChange = (capability: string, value: number) => {
    setModel(prev => ({
      ...prev,
      capabilities: {
        ...prev.capabilities,
        [capability]: value
      }
    }));
  };

  const rankConflict = model.ranked_position != null
    ? existingModels.find(m => m.ranked_position === model.ranked_position)
    : null;

  const isDuplicateModel = !!(model.providerModelId && model.customEndpointUrl &&
    existingModels.some(
      m => m.providerModelId === model.providerModelId && m.customEndpointUrl === model.customEndpointUrl
    ));

  const canProceed = () => {
    switch (currentStep) {
      case 'essentials':
        return model.name && model.providerModelId && model.provider && !rankConflict;
      case 'capabilities':
        return true; // Capabilities can be 0
      case 'endpoint':
        return model.customEndpointUrl && model.customApiKey;
      case 'review':
        return true;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (!canProceed()) {
      setError('Please fill in all required fields');
      return;
    }

    setError('');
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < totalSteps) {
      setCurrentStep(steps[nextIndex]);
    }
  };

  const handlePrevious = () => {
    setError('');
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  };

  const handleTestConnection = async () => {
    if (!model.customEndpointUrl || !model.customApiKey) {
      setError('Please provide both endpoint URL and API key');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');
    setError('');

    try {
      const response = await apiService.post('/portal/models/test-connection', {
        url: model.customEndpointUrl,
        apiKey: model.customApiKey,
        headers: model.customHeaders,
        modelId: model.providerModelId || 'test'
      });

      setConnectionStatus('success');
    } catch (err: any) {
      setConnectionStatus('error');
      const errorMessage = err.response?.data?.error || err.message || 'Connection failed';
      setError(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    if (!canProceed()) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError('');

    const errorMessage = await onSave(model);

    setSaving(false);

    if (!errorMessage) {
      onClose();
    } else {
      setError(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="pk-scrim" style={{ position: 'fixed', zIndex: 9999, padding: 16 }} onClick={onClose}>
      <div className="pk-modal" style={{ maxWidth: 720, height: '95vh' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="pk-modal-head" style={{ justifyContent: 'space-between' }}>
          <div className="col">
            <h2 className="pk-h3">Add New Model</h2>
            <p className="pk-sub">Step {currentStepIndex + 1} of {totalSteps}</p>
          </div>
          <button type="button" onClick={onClose} className="pk-btn pk-btn-quiet pk-btn-icon"><X size={20} /></button>
        </div>

        {/* Progress Bar */}
        <div className="pk-steps">
          {steps.map((step, index) => (
            <React.Fragment key={step}>
              <div className={`pk-step ${index === currentStepIndex ? 'is-current' : ''} ${index < currentStepIndex ? 'is-done' : ''}`}>
                <span className="n">{index < currentStepIndex ? <Check size={13} /> : index + 1}</span>
                <span className="name" style={{ textTransform: 'capitalize' }}>{step === 'essentials' ? 'Essentials' : step}</span>
              </div>
              {index < steps.length - 1 && <div className={`pk-step-line ${index < currentStepIndex ? 'is-done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* Error Display */}
        {error && (
          <div className="pk-note pk-note-danger" style={{ margin: '12px 16px 0' }}>
            {error}
          </div>
        )}

        {/* Content */}
        <div className="pk-modal-body" style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {currentStep === 'essentials' && (
            <EssentialsStep
              model={model}
              onFieldChange={handleFieldChange}
              isAdmin={isAdmin}
              nextRankPosition={getNextRankPosition()}
              rankConflict={rankConflict}
            />
          )}

          {currentStep === 'capabilities' && (
            <CapabilitiesStep
              capabilities={model.capabilities || {}}
              onCapabilityChange={handleCapabilityChange}
            />
          )}

          {currentStep === 'endpoint' && (
            <EndpointStep
              model={model}
              onFieldChange={handleFieldChange}
              onTestConnection={handleTestConnection}
              isDuplicate={isDuplicateModel}
              testingConnection={testingConnection}
              connectionStatus={connectionStatus}
              error={error}
            />
          )}

          {currentStep === 'review' && (
            <ReviewStep model={model} />
          )}
        </div>

        {/* Footer */}
        <div className="pk-modal-foot">
          <button type="button" onClick={handlePrevious} disabled={currentStepIndex === 0} className="pk-btn pk-btn-outline">
            <ArrowLeft size={16} />
            Previous
          </button>

          <div className="spacer" />

          {currentStepIndex < totalSteps - 1 ? (
            <button type="button" onClick={handleNext} disabled={!canProceed()} className="pk-btn pk-btn-ink">
              Next Step
              <ArrowRight size={16} />
            </button>
          ) : (
            <button type="button" onClick={handleSave} disabled={!canProceed() || saving} className="pk-btn pk-btn-grad">
              {saving ? 'Saving...' : 'Save Model'}
              <Check size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Step Components
function EssentialsStep({ model, onFieldChange, isAdmin, nextRankPosition, rankConflict }: any) {
  const [showTooltip, setShowTooltip] = useState<string | null>(null);

  const tooltips: Record<string, string> = {
    name: 'A friendly display name for this model',
    providerModelId: 'Unique identifier used by the provider (e.g., gpt-4, claude-3-sonnet)',
    contextWindow: 'Maximum number of tokens the model can process in input',
    maxTokens: 'Maximum number of tokens the model can generate in output',
    category: 'Model category for filtering and organization',
    ranked_position: 'Global ranking position (1 = best)',
    price_per_token: 'Pricing format: "$1.25/1M in, $10/1M out" or "free". System averages input and output costs for price priority sorting.',
    vision: 'Model can process and understand images',
    streaming: 'Model supports real-time streaming responses',
    functionCalling: 'Model supports OpenAI-compatible tool/function calling (required for tools like OpenClaw)',
    privacy: 'Model is privacy-compliant for sensitive data',
    uncensored: 'Model handles sensitive content without restrictions',
    taskDet: 'Use this model for task determination decisions',
    premium: 'Model requires premium subscription',
  };

  const InfoIcon = ({ field }: { field: string; position?: "above" | "below" }) => (
    <span style={{ position: 'relative', display: 'inline-flex', marginLeft: 4 }}>
      <Info
        size={13}
        className="pk-i"
        style={{ cursor: 'help' }}
        onMouseEnter={() => setShowTooltip(field)}
        onMouseLeave={() => setShowTooltip(null)}
      />
      {showTooltip === field && (
        <span
          className="pk-small"
          style={{
            position: 'absolute', left: 0, top: 20, zIndex: 60, width: 224, padding: '7px 9px',
            background: 'var(--arc-ink)', color: 'var(--arc-paper)', borderRadius: 'var(--arc-r-sm)',
            boxShadow: 'var(--arc-sh-2)', lineHeight: 1.5,
          }}
        >
          {tooltips[field]}
        </span>
      )}
    </span>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 className="pk-h3" style={{ marginBottom: 4 }}>Let's start with the basics</h3>
        <p className="pk-sub">Tell us about your model. We'll use this information to identify and route requests.</p>
      </div>

      <div className="pk-form-grid">
        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="pk-field">
            <label className="pk-field-label">Model Name <span className="req">*</span><InfoIcon field="name" /></label>
            <input
              type="text"
              value={model.name}
              onChange={(e) => onFieldChange('name', e.target.value)}
              placeholder="GPT-4 Turbo"
              className="pk-input"
            />
          </div>

          <div className="pk-field">
            <label className="pk-field-label">Provider Model ID <span className="req">*</span><InfoIcon field="providerModelId" /></label>
            <input
              type="text"
              value={model.providerModelId}
              onChange={(e) => onFieldChange('providerModelId', e.target.value)}
              placeholder="gpt-4-turbo"
              className="pk-input"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="pk-field">
              <label className="pk-field-label">Category<InfoIcon field="category" /></label>
              <select value={model.category} onChange={(e) => onFieldChange('category', e.target.value)} className="pk-select">
                <option value="">Select...</option>
                <option value="general">General</option>
                <option value="reasoning">Reasoning</option>
                <option value="coding">Coding</option>
                <option value="creative">Creative</option>
                <option value="vision">Vision</option>
              </select>
            </div>

            <div className="pk-field">
              <label className="pk-field-label">Rank Position<InfoIcon field="ranked_position" /></label>
              <input
                type="number"
                value={model.ranked_position}
                onChange={(e) => onFieldChange('ranked_position', parseInt(e.target.value))}
                placeholder={nextRankPosition.toString()}
                className="pk-input"
                aria-invalid={!!rankConflict}
              />
              {rankConflict && (
                <p className="pk-error">Rank {model.ranked_position} is already used by "{rankConflict.name}".</p>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div className="pk-field">
              <label className="pk-field-label">Context Window<InfoIcon field="contextWindow" /></label>
              <input
                type="number"
                value={model.contextWindow}
                onChange={(e) => onFieldChange('contextWindow', parseInt(e.target.value))}
                placeholder="8192"
                className="pk-input"
              />
            </div>

            <div className="pk-field">
              <label className="pk-field-label">Max Tokens<InfoIcon field="maxTokens" /></label>
              <input
                type="number"
                value={model.maxTokens}
                onChange={(e) => onFieldChange('maxTokens', parseInt(e.target.value))}
                placeholder="4096"
                className="pk-input"
              />
            </div>
          </div>

          <div className="pk-field">
            <label className="pk-field-label">Price/Token<InfoIcon field="price_per_token" /></label>
            <input
              type="text"
              value={model.price_per_token}
              onChange={(e) => onFieldChange('price_per_token', e.target.value)}
              placeholder="$1.25/1M in, $10/1M out or Free"
              className="pk-input"
            />
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="pk-field">
            <label className="pk-field-label">Provider <span className="req">*</span></label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {isAdmin && (
                <>
                  <ProviderChoice label="OpenRouter" selected={model.provider === 'openrouter'} onClick={() => onFieldChange('provider', 'openrouter')} icon={<Shuffle size={16} />} />
                  <ProviderChoice label="Google" selected={model.provider === 'google'} onClick={() => onFieldChange('provider', 'google')} icon={<Cloud size={16} />} />
                  <ProviderChoice label="Venice" selected={model.provider === 'venice'} onClick={() => onFieldChange('provider', 'venice')} icon={<Sparkles size={16} />} />
                </>
              )}
              <ProviderChoice
                label="Custom"
                selected={model.provider === 'custom'}
                onClick={() => onFieldChange('provider', 'custom')}
                icon={<Plug size={16} />}
                meta={!isAdmin ? 'Your API' : undefined}
              />
            </div>
            {!isAdmin && <p className="pk-help" style={{ marginTop: 4 }}>Regular users can only add custom endpoint models</p>}
          </div>

          <div className="pk-field">
            <label className="pk-field-label">Description</label>
            <textarea
              value={model.description}
              onChange={(e) => onFieldChange('description', e.target.value)}
              placeholder="Latest GPT-4 model with improved performance and longer context..."
              className="pk-textarea"
              rows={3}
            />
          </div>

          {/* Checkboxes */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <label className="pk-check">
              <input type="checkbox" checked={model.supports_vision} onChange={(e) => onFieldChange('supports_vision', e.target.checked)} />
              Vision<InfoIcon field="vision" />
            </label>
            <label className="pk-check">
              <input type="checkbox" checked={model.supports_streaming} onChange={(e) => onFieldChange('supports_streaming', e.target.checked)} />
              Streaming<InfoIcon field="streaming" />
            </label>
            <label className="pk-check">
              <input type="checkbox" checked={model.functionCalling || false} onChange={(e) => onFieldChange('functionCalling', e.target.checked)} />
              Function Calling<InfoIcon field="functionCalling" />
            </label>
            <label className="pk-check">
              <input type="checkbox" checked={model.isPrivacyCompliant} onChange={(e) => onFieldChange('isPrivacyCompliant', e.target.checked)} />
              Privacy<InfoIcon field="privacy" />
            </label>
            <label className="pk-check">
              <input type="checkbox" checked={model.isUncensored} onChange={(e) => onFieldChange('isUncensored', e.target.checked)} />
              Uncensored<InfoIcon field="uncensored" />
            </label>
            <label className="pk-check">
              <input type="checkbox" checked={model.taskDeterminationModel} onChange={(e) => onFieldChange('taskDeterminationModel', e.target.checked)} />
              Task Det<InfoIcon field="taskDet" />
            </label>
            <label className="pk-check">
              <input type="checkbox" checked={model.requires_premium} onChange={(e) => onFieldChange('requires_premium', e.target.checked)} />
              Premium<InfoIcon field="premium" />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}

function ProviderChoice({ label, selected, onClick, icon, meta }: any) {
  return (
    <button type="button" role="radio" aria-checked={selected} onClick={onClick} className={`pk-choice ${selected ? 'is-selected' : ''}`}>
      <span className="pk-i">{icon}</span>
      <span>{label}</span>
      {meta && <span className="meta">{meta}</span>}
    </button>
  );
}

function CapabilitiesStep({ capabilities, onCapabilityChange }: any) {
  const capabilityConfig = [
    { key: 'reasoning', label: 'Reasoning', icon: <Brain size={16} />, description: 'Logical analysis and problem-solving' },
    { key: 'coding', label: 'Coding', icon: <Code size={16} />, description: 'Programming and code generation' },
    { key: 'creativity', label: 'Creativity', icon: <Palette size={16} />, description: 'Creative writing and ideation' },
    { key: 'conversational', label: 'Conversational', icon: <MessageCircle size={16} />, description: 'Natural dialogue and chat' },
    { key: 'speed', label: 'Speed', icon: <Zap size={16} />, description: 'Response time and efficiency' },
    { key: 'webSearch', label: 'Web Search', icon: <Search size={16} />, description: 'Internet search capabilities' },
    { key: 'enhancedReasoning', label: 'Enhanced Reasoning', icon: <BrainCircuit size={16} />, description: 'Deep analytical thinking' },
    { key: 'imageGeneration', label: 'Image Generation', icon: <ImageIcon size={16} />, description: 'Creating images from text' },
  ];

  const leftColumn = capabilityConfig.slice(0, 4);
  const rightColumn = capabilityConfig.slice(4);

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 className="pk-h3" style={{ marginBottom: 4 }}>How capable is this model?</h3>
        <p className="pk-sub">Rate each capability from 0 (not capable) to 5 (excellent). This helps us route requests to the best model.</p>
      </div>

      <div className="pk-note pk-note-accent" style={{ marginBottom: 16 }}>
        <Info size={14} />
        <p className="pk-small"><strong>Tip:</strong> Set capabilities that match the model's strengths. Lower scores are okay - they help us choose the right model for each task.</p>
      </div>

      <div className="pk-form-grid">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {leftColumn.map(({ key, label, icon, description }) => (
            <CapabilityRating key={key} label={label} icon={icon} description={description} value={capabilities[key] || 0} onChange={(value) => onCapabilityChange(key, value)} />
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rightColumn.map(({ key, label, icon, description }) => (
            <CapabilityRating key={key} label={label} icon={icon} description={description} value={capabilities[key] || 0} onChange={(value) => onCapabilityChange(key, value)} />
          ))}
        </div>
      </div>
    </div>
  );
}

interface CapabilityRatingProps {
  label: string;
  icon: React.ReactNode;
  description: string;
  value: number;
  onChange: (value: number) => void;
}

function CapabilityRating({ label, icon, description, value, onChange }: CapabilityRatingProps) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div className="pk-i-plate">{icon}</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--arc-ink)' }}>{label}</div>
          <div className="pk-small">{description}</div>
        </div>
      </div>

      <div className="pk-rating">
        <span className="pips">
          {[1, 2, 3, 4, 5].map(pip => (
            <button
              key={pip}
              type="button"
              className={pip <= value ? 'on' : ''}
              onClick={() => onChange(value === pip ? pip - 1 : pip)}
              aria-label={`${label} rating ${pip}`}
            >
              {pip}
            </button>
          ))}
        </span>
        <span className="val">{value}/5</span>
      </div>
    </div>
  );
}

function EndpointStep({ model, onFieldChange, onTestConnection, testingConnection, connectionStatus, error, isDuplicate }: any) {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h3 className="pk-h3" style={{ marginBottom: 4 }}>Custom Endpoint Configuration</h3>
        <p className="pk-sub">Connect your own API endpoint. We'll encrypt your API key for security.</p>
      </div>

      <div className="pk-note pk-note-accent" style={{ marginBottom: 16 }}>
        <Lock size={14} />
        <p className="pk-small">Your endpoint must be OpenAI-compatible (same request/response format)</p>
      </div>

      <div className="pk-field" style={{ marginBottom: 14 }}>
        <label className="pk-field-label">Endpoint URL <span className="req">*</span></label>
        <input
          type="url"
          value={model.customEndpointUrl}
          onChange={(e) => onFieldChange('customEndpointUrl', e.target.value)}
          placeholder="https://api.example.com/v1/chat/completions"
          autoComplete="new-password"
          name="endpoint-url"
          className="pk-input"
        />
        <p className="pk-help">Full URL including the path</p>
        {isDuplicate && (
          <div className="pk-note pk-note-warning" style={{ marginTop: 8 }}>
            <Info size={14} />
            <p className="pk-small">A model with this ID and endpoint URL already exists. You can still add it as a separate entry.</p>
          </div>
        )}
      </div>

      <div className="pk-field" style={{ marginBottom: 14 }}>
        <label className="pk-field-label">API Key <span className="req">*</span></label>
        <input
          type="password"
          value={model.customApiKey}
          onChange={(e) => onFieldChange('customApiKey', e.target.value)}
          placeholder="sk-..."
          autoComplete="new-password"
          name="api-key"
          className="pk-input"
        />
        <p className="pk-help">Will be encrypted when saved</p>
      </div>

      <div className="pk-field" style={{ marginBottom: 14 }}>
        <label className="pk-field-label">Custom Headers (JSON)</label>
        <textarea
          value={typeof model.customHeaders === 'string'
            ? model.customHeaders
            : JSON.stringify(model.customHeaders || {}, null, 2)}
          onChange={(e) => onFieldChange('customHeaders', e.target.value)}
          placeholder='{\n  "X-Custom-Header": "value"\n}'
          className="pk-textarea pk-input-mono"
          rows={4}
        />
        <p className="pk-help">Optional: Additional headers to include in API requests</p>
      </div>

      <div>
        <button
          type="button"
          onClick={onTestConnection}
          disabled={testingConnection || !model.customEndpointUrl || !model.customApiKey}
          className="pk-btn pk-btn-outline"
        >
          {testingConnection ? (
            <>
              <span className="pk-spinner" />
              Testing...
            </>
          ) : connectionStatus === 'success' ? (
            <>
              <Check size={16} style={{ color: 'var(--arc-success)' }} />
              Connection Successful
            </>
          ) : connectionStatus === 'error' ? (
            <>
              <X size={16} style={{ color: 'var(--arc-danger)' }} />
              Connection Failed
            </>
          ) : (
            'Test Connection'
          )}
        </button>

        {connectionStatus === 'error' && error && (
          <div className="pk-note pk-note-danger" style={{ marginTop: 8 }}>{error}</div>
        )}
      </div>
    </div>
  );
}

function ReviewStep({ model }: any) {
  const features = [
    { label: 'Vision', on: model.supports_vision },
    { label: 'Streaming', on: model.supports_streaming },
    { label: 'Function Calling', on: model.functionCalling },
    { label: 'Privacy', on: model.isPrivacyCompliant },
    { label: 'Uncensored', on: model.isUncensored },
    { label: 'Task Det', on: model.taskDeterminationModel },
    { label: 'Premium', on: model.requires_premium },
  ];

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h3 className="pk-h3" style={{ marginBottom: 4 }}>Review Your Model</h3>
        <p className="pk-sub">Check everything looks good before saving.</p>
      </div>

      <div className="pk-card-inset" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="pk-review">
          <ReviewField label="Model Name" value={model.name} />
          <ReviewField label="Provider Model ID" value={model.providerModelId} />
          <ReviewField label="Provider" value={model.provider} />
          <ReviewField label="Category" value={model.category || 'Not set'} />
          <ReviewField label="Rank Position" value={model.ranked_position || 'Not set'} />
          <ReviewField label="Context Window" value={model.contextWindow || 'Not set'} />
          <ReviewField label="Max Tokens" value={model.maxTokens || 'Not set'} />
          <ReviewField label="Price/Token" value={model.price_per_token || 'Not set'} />
        </div>

        {model.description && (
          <div style={{ paddingTop: 16, borderTop: '1px solid var(--arc-line-2)' }}>
            <ReviewField label="Description" value={model.description} />
          </div>
        )}

        <div style={{ paddingTop: 16, borderTop: '1px solid var(--arc-line-2)' }}>
          <div className="pk-h4" style={{ marginBottom: 8 }}>Features</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 8 }}>
            {features.map(f => (
              <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {f.on ? <Check size={14} className="pk-tick" /> : <X size={14} className="pk-cross" />}
                <span className="pk-small" style={{ color: 'var(--arc-ink-2)' }}>{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ paddingTop: 16, borderTop: '1px solid var(--arc-line-2)' }}>
          <div className="pk-h4" style={{ marginBottom: 8 }}>Capabilities</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {Object.entries(model.capabilities || {}).map(([key, value]) => (
              <div key={key} style={{ display: 'flex', justifyContent: 'space-between' }} className="pk-small">
                <span style={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').trim()}:</span>
                <span style={{ fontWeight: 700, color: 'var(--arc-ink-2)' }}>{value as number}/5</span>
              </div>
            ))}
          </div>
        </div>

        {model.provider === 'custom' && (
          <div style={{ paddingTop: 16, borderTop: '1px solid var(--arc-line-2)' }}>
            <div className="pk-h4" style={{ marginBottom: 8 }}>Custom Endpoint</div>
            <ReviewField label="URL" value={model.customEndpointUrl} small />
            <div style={{ marginTop: 8 }}><ReviewField label="API Key" value="••••••••••••" small /></div>
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewField({ label, value, small }: any) {
  return (
    <div className={small ? 'pk-small' : 'row'}>
      <div className="k pk-label" style={{ fontSize: small ? 11 : undefined }}>{label}</div>
      <div style={{ fontWeight: 700, color: 'var(--arc-ink)', wordBreak: 'break-word', marginTop: small ? 2 : 0 }}>{value || 'Not set'}</div>
    </div>
  );
}
